import { memo } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck } from "@tabler/icons-react";

import { Menu, Tooltip } from "@fragments";

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const speedLabel = (t, rate) => (rate === 1 ? t("player.speed.normal") : `${rate}×`);

/**
 * SpeedMenu — playback-rate picker; the trigger chip reads the current rate.
 *
 * Menu clones its direct child to wire the open trigger, and Tooltip clones
 * its child for hover — so the wiring is: Menu → span (click anchor) →
 * Tooltip → button. The button's click bubbles to the span, which opens the
 * menu; the bar-level click shield keeps it from reaching the stage.
 */
function SpeedMenu({ speed, onSetSpeed, disabled = false }) {
    const { t } = useTranslation("content");

    const items = SPEED_OPTIONS.map((rate) => ({
        id: `speed-${rate}`,
        label: speedLabel(t, rate),
        icon: rate === speed ? <IconCheck size={16} /> : null,
        active: rate === speed,
        onClick: () => onSetSpeed(rate),
    }));

    // A disabled button dispatches no click, so the anchor span never opens
    // the menu; the guard below covers keyboard/synthetic activation too.
    return (
        <Menu items={items} placement="top-start">
            <span className="player-speed-anchor" onClickCapture={disabled ? (event) => event.stopPropagation() : undefined}>
                <Tooltip content={t("player.speed.tooltip")} placement="top">
                    <button
                        type="button"
                        aria-label={t("player.speed.tooltip")}
                        className="player-control-button player-speed-button"
                        disabled={disabled}
                    >
                        {speed === 1 ? "1×" : `${speed}×`}
                    </button>
                </Tooltip>
            </span>
        </Menu>
    );
}

export default memo(SpeedMenu);
